import React from 'react';
import CustomDropdown from './CustomDropdown';
import FormFieldLabel from './FormFieldLabel';

// Option interface for dropdowns
export interface FieldOption {
    value: string;
    label: string;
}

interface FormFieldProps {
    id: string;
    label: string;
    value: string;
    onChange: (
        // Allow broader type for event, or stick to specific target for custom components
        e:
            | { target: { value: string } }
            | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => void;
    placeholder?: string;
    type?: 'text' | 'textarea' | 'dropdown' | 'voluntary-dropdown';
    rows?: number; // For textarea
    required?: boolean;
    options?: FieldOption[]; // For dropdowns
    emptyOptionLabel?: string; // For voluntary-dropdown, the text to show for "no selection"
}

const FormField: React.FC<FormFieldProps> = ({
    id,
    label,
    value,
    onChange,
    placeholder = '',
    type = 'text',
    rows = 3,
    required = false,
    options = [],
    emptyOptionLabel = 'Select an option',
}) => {
    // Handle direct value change from CustomDropdown
    const handleDropdownChange = (newValue: string) => {
        onChange({ target: { value: newValue } });
    };

    const renderField = () => {
        switch (type) {
            case 'textarea':
                return (
                    <textarea
                        id={id}
                        value={value}
                        // Cast to any for now if type mismatch, or adjust onChange prop type
                        onChange={onChange as any}
                        placeholder={placeholder}
                        rows={rows}
                        required={required}
                    />
                );
            case 'dropdown': // This is for Table Spec - required selection, no custom text
                return (
                    <CustomDropdown
                        options={options}
                        value={value}
                        onChange={handleDropdownChange}
                        placeholder={emptyOptionLabel} // Should be more like "Select..." as it's required
                        required={true}
                        allowCustomText={false}
                    />
                );
            case 'voluntary-dropdown': // This is for Text Spec - can be custom text or selection
                return (
                    <CustomDropdown
                        options={options}
                        value={value}
                        onChange={handleDropdownChange}
                        placeholder={placeholder || emptyOptionLabel} // Placeholder for typing or guiding selection
                        required={false} // Overall field might not be required, but selection from list isn't forced if custom text is allowed
                        allowCustomText={true}
                    />
                );
            default: // text input
                return (
                    <input
                        type="text"
                        id={id}
                        value={value}
                        onChange={onChange as any}
                        placeholder={placeholder}
                        required={required}
                    />
                );
        }
    };

    return (
        <div className="form-field">
            <FormFieldLabel htmlFor={id} label={label} required={required} />
            {renderField()}
        </div>
    );
};

export default FormField;
